<template>
  <div class="compare-table-pivot">
    <div v-if="records.length === 0" class="empty-state">
      <i class="fas fa-table" aria-hidden="true"></i>
      <p>Geen records gevonden</p>
    </div>

    <div v-else class="table-responsive">
      <table class="table table-bordered table-hover">
        <caption class="visually-hidden">
          Kruistabel: elke rij is een kenmerk, elke kolom een project.
        </caption>
        <thead class="table-light">
          <tr>
            <th scope="col" class="field-header">{{ firstFieldLabel }}</th>
            <th scope="col" v-for="record in records" :key="record.id" class="record-header">
              {{ formatValue(record[displayFields[0]]) }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="field in displayFields.slice(1)" :key="field">
            <th scope="row" class="field-name">{{ getFieldLabel(field) }}</th>
            <td v-for="record in records" :key="record.id" class="field-value">
              {{ formatValue(record[field]) }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue';

export default defineComponent({
  name: 'CompareTablePivot',
  props: {
    records: {
      type: Array as PropType<any[]>,
      required: true
    },
    displayFields: {
      type: Array as PropType<string[]>,
      required: true
    },
    fieldLabels: {
      type: Object as PropType<Record<string, string>>,
      default: () => ({})
    }
  },
  computed: {
    firstFieldLabel(): string {
      return this.displayFields.length > 0 
        ? this.getFieldLabel(this.displayFields[0])
        : 'Veld';
    }
  },
  methods: {
    formatValue(value: any): string {
      if (value === null || value === undefined) return '-';
      if (Array.isArray(value)) return value.join(', ');
      if (typeof value === 'object') return JSON.stringify(value);
      return String(value);
    },

    getFieldLabel(fieldName: string): string {
      return this.fieldLabels[fieldName] || fieldName;
    }
  }
});
</script>

<style scoped>
.compare-table-pivot {
  background: white;
  border-radius: 8px;
  padding: 1rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.table-responsive {
  overflow-x: auto;
}

.table {
  margin-bottom: 0;
  font-size: 0.9rem;
}

.table-bordered {
  border: 2px solid #dee2e6;
}

.field-header,
.field-name {
  background-color: #f8f9fa;
  font-weight: 600;
  color: var(--primary, #0f6c65);
  position: sticky;
  left: 0;
  z-index: 10;
  min-width: 200px;
  white-space: nowrap;
}

.field-header {
  background-color: var(--primary, #0f6c65);
  color: white;
  font-weight: bold;
  text-align: center;
  vertical-align: middle;
}

.record-header {
  background-color: var(--primary, #0f6c65);
  color: white;
  font-weight: 600;
  text-align: center;
  min-width: 150px;
  max-width: 200px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  vertical-align: middle;
}

.field-name {
  font-weight: 600;
  border-right: 2px solid #dee2e6;
}

.field-value {
  text-align: center;
  vertical-align: middle;
  padding: 0.75rem;
}

.table-hover tbody tr:hover {
  background-color: rgba(0, 0, 0, 0.035);
}

.empty-state {
  text-align: center;
  padding: 3rem;
  color: #6c757d;
}

.empty-state i {
  font-size: 3rem;
  opacity: 0.3;
  margin-bottom: 1rem;
}

/* Zorg dat sticky kolom werkt bij horizontal scroll */
.table-responsive {
  position: relative;
}

/* De eerste cel van elke rij is een <th scope="row"> — dat maakt de kruistabel
   navigeerbaar met een schermlezer, dus de selector mag niet op td blijven. */
thead th:first-child,
tbody tr > *:first-child {
  background-color: #f8f9fa;
}

/* Een th in de body erft vetgedrukt en gecentreerd; hier hoort hij zich als de
   bestaande rijkop te gedragen. */
tbody th.field-name {
  font-weight: 600;
  text-align: right;
}

/* Deze regel wint van .field-header, dus de hoekcel houdt de lichte
   achtergrond maar hield ook de witte letters van .field-header — onleesbaar.
   Daar hoort de accentkleur. */
thead th:first-child {
  color: var(--primary, #0f6c65);
}
</style>

